import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AgencyNavbar from '../../Pages/Navbar/AgencyNavbar';
import { Container, Row, Col, Button, Table, OverlayTrigger, Popover } from "react-bootstrap";
import { FaEye, FaEdit, FaTrash, FaEnvelope, FaPhone, FaWhatsapp, FaTelegram, FaComment, FaUserPlus, FaSync } from "react-icons/fa";
import BaseUrl from '../../ApiServices/BaseUrl';
import Swal from 'sweetalert2';
import './AddJobSeeker.css';

function AddJobSeeker() {
  const navigate = useNavigate();
  const [jobSeekers, setJobSeekers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agencyData, setAgencyData] = useState(null);
  const [agencyLoading, setAgencyLoading] = useState(true);
  const [agencyCurrentSub, setAgencyCurrentSub] = useState(null);
  const [agencyCurrentFeatures, setAgencyCurrentFeatures] = useState([]);
  const [repostingUserId, setRepostingUserId] = useState(null);

  useEffect(() => {
    const fetchAgencyData = async () => {
      try {
        const currentUser = JSON.parse(localStorage.getItem('user'));
        const userId = currentUser?.id;

        if (!userId) {
          console.error('No user ID found in local storage');
          setAgencyLoading(false);
          return;
        }

        console.log('Fetching agency data for user ID:', userId);
        const agencyResponse = await fetch(`${BaseUrl}/api/agency/${userId}`);
        if (!agencyResponse.ok) {
          throw new Error('Failed to fetch agency data');
        }
        const agencyData = await agencyResponse.json();
        console.log('Agency data fetched:', agencyData);
        setAgencyData(agencyData);

        const subscriptionResponse = await fetch(`${BaseUrl}/api/agency/subscription/${userId}?category=posting`);
        if (subscriptionResponse.ok) {
          const subscriptionData = await subscriptionResponse.json();
          setAgencyCurrentSub(subscriptionData.subscription || null);
          setAgencyCurrentFeatures(subscriptionData.features || []);
        }
      } catch (err) {
        console.error('Error fetching agency data:', err);
        setError('Failed to load agency verification status');
      } finally {
        setAgencyLoading(false);
      }
    };

    fetchAgencyData();
  }, []);

  useEffect(() => {
    const fetchJobSeekers = async () => {
      try {
        // Get current logged-in agency user ID
        const currentUser = JSON.parse(localStorage.getItem('user'));
        const currentAgencyId = currentUser?.id;

        console.log('Current agency user ID from localStorage:', currentAgencyId);

        if (!currentAgencyId) {
          setError('No agency user found. Please login again.');
          setLoading(false);
          return;
        }

        // Fetch from job-seeker API endpoint
        console.log('Fetching job seekers from:', `${BaseUrl}/api/job-seeker`);
        const response = await fetch(`${BaseUrl}/api/job-seeker`);
        if (!response.ok) {
          throw new Error('Failed to fetch job seekers');
        }

        const data = await response.json();
        console.log('Total job seekers fetched:', data.length);

        // Log the first job seeker to see the structure
        if (data.length > 0) {
          console.log('Sample job seeker data structure:', data[0]);
        }

        // Filter job seekers by agency_uid matching current agency ID
        const agencyJobSeekers = data.filter(user => {
          return Number(user.agency_uid) === Number(currentAgencyId);
        });

        console.log('Filtered agency job seekers:', agencyJobSeekers.length);

        setJobSeekers(agencyJobSeekers);
      } catch (err) {
        console.error('Error fetching job seekers:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchJobSeekers();
  }, []);

  const handleDeleteJobSeeker = async (jobSeeker) => {
    // Show confirmation dialog
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete ${jobSeeker.first_name} ${jobSeeker.last_name}. This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      // Show loading indicator
      Swal.fire({
        title: 'Deleting...',
        text: 'Please wait while we delete the candidate',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Use the combined delete API that deletes from both tables
      // The API endpoint is /api/user/:id where :id is the user_id
      console.log('Deleting user with ID:', jobSeeker.user_id);
      const response = await fetch(`${BaseUrl}/api/user/${jobSeeker.user_id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to delete candidate');
      }

      // Remove from local state
      setJobSeekers(prev => prev.filter(js => js.id !== jobSeeker.id));

      // Show success message
      Swal.fire({
        icon: 'success',
        title: 'Deleted!',
        text: 'Candidate has been deleted successfully.',
        timer: 2000,
        showConfirmButton: false
      });

    } catch (err) {
      console.error('Error deleting job seeker:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.message || 'Failed to delete candidate. Please try again.',
        confirmButtonColor: '#d33'
      });
    }
  };

  const handleRegisterClick = async () => {
    if (agencyLoading) {
      Swal.fire({
        icon: 'info',
        title: 'Please wait...',
        text: 'Checking verification status',
        showConfirmButton: false,
        allowOutsideClick: false
      });
      return;
    }

    const currentUser = JSON.parse(localStorage.getItem('user'));
    const userId = currentUser?.id;
    const userEmail = currentUser?.email;

    console.log('Current user data from local storage:', { userId, userEmail });

    if (!userId || !userEmail) {
      console.error('No user ID or email found in local storage');
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'User information not found. Please login again.',
        confirmButtonColor: '#d33'
      });
      return;
    }

    if (!agencyData) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Unable to verify agency status. Please try again.',
        confirmButtonColor: '#d33'
      });
      return;
    }

    if (agencyData.is_verified === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Admin Approval Required',
        html: `
          <div class="text-center">
            <p>Your agency account is pending verification.</p>
            <div class="alert alert-info mt-2 p-2 small">
              <strong>Note:</strong> Please wait for admin approval before registering job seekers.
            </div>
          </div>
        `,
        confirmButtonColor: '#f27474',
        confirmButtonText: 'OK'
      });
      return;
    }

    // Check subscription limits
    try {
      const featureCheck = await axios.get(`${BaseUrl}/api/agency/check-feature`, {
        params: { agencyId: userId, feature: 'candidate_posting' }
      });

      if (!featureCheck.data.allowed) {
        Swal.fire({
          icon: 'error',
          title: 'Subscription Limit Reached',
          text: featureCheck.data.message || 'You have reached your candidate registration limit.',
          confirmButtonColor: '#d33',
          showCancelButton: true,
          confirmButtonText: 'View Plans',
          cancelButtonText: 'Cancel'
        }).then((result) => {
          if (result.isConfirmed) {
            navigate('/a-jobsubscriptionplans');
          }
        });
        return;
      }
    } catch (err) {
      console.error('Error checking candidate_post feature:', err);
      // Don't block if API fails unexpectedly, but log it
    }

    console.log('Agency is verified, proceeding with registration');
    navigate('/jobseekerregistration', {
      state: {
        agencyUserId: userId,
        agencyUserEmail: userEmail
      }
    });
  };

  const handleUpdateResumeClick = (jobSeeker) => {
    if (agencyData && agencyData.is_verified === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Admin Approval Required',
        html: `
          <div class="text-center">
            <p>Your agency account is pending verification.</p>
            <div class="alert alert-info mt-2 p-2 small">
              <strong>Note:</strong> Please wait for admin approval before updating job seeker resumes.
            </div>
          </div>
        `,
        confirmButtonColor: '#f27474',
        confirmButtonText: 'OK'
      });
      return;
    }

    // Check if user_id exists in the jobSeeker object
    if (!jobSeeker.user_id) {
      console.error('No user_id found in job seeker data:', jobSeeker);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Cannot update: User ID not found. Please try again or contact support.',
        confirmButtonColor: '#d33'
      });
      return;
    }

    console.log('Navigating to update resume with user_id:', jobSeeker.user_id);
    console.log('Job seeker data:', jobSeeker);

    // IMPORTANT: Pass the user_id (from users table) to the update page
    // This ensures we update the correct user record
    navigate('/a-personalinfo', {
      state: {
        userId: jobSeeker.user_id, // This is the ID from users table
        jobSeekerId: jobSeeker.id, // This is the ID from job_seekers table (optional, for reference)
        userData: {
          ...jobSeeker,
          // Ensure we pass the user_id for update operations
          id: jobSeeker.user_id // Override id with user_id for the update page
        }
      }
    });
  };

  const handleViewResumeClick = (jobSeeker) => {
    // Use user_id from the jobSeeker object instead of job_seeker id
    navigate(`/a-agencyviewcandidates/${jobSeeker.user_id}`);
  };

  const hasAgencyPremiumRepostAccess = () => {
    return agencyCurrentFeatures.some(feature =>
      (feature.feature_key === 'agency_repost_access' || feature.feature_key === 'agency_candidate_repost_access') && feature.is_enabled
    );
  };

  const getRepostCooldownEnd = (item) => {
    const lastRepostedAt = item?.last_reposted_at || item?.reposted_at;
    if (!lastRepostedAt) return null;

    const nextAvailableAt = new Date(lastRepostedAt);
    if (Number.isNaN(nextAvailableAt.getTime())) return null;

    nextAvailableAt.setDate(nextAvailableAt.getDate() + 7);
    return nextAvailableAt;
  };

  const isInRepostCooldown = (item) => {
    const nextAvailableAt = getRepostCooldownEnd(item);
    return !!nextAvailableAt && nextAvailableAt > new Date();
  };

  const handleRepostJobSeeker = async (jobSeeker) => {
    const currentUser = JSON.parse(localStorage.getItem('user'));
    const agencyId = currentUser?.id;

    if (!agencyId) {
      Swal.fire('Error', 'Agency user not found. Please login again.', 'error');
      return;
    }

    if (!hasAgencyPremiumRepostAccess()) {
      Swal.fire({
        icon: 'info',
        title: 'Upgrade Required',
        text: 'Repost is not enabled in your current agency posting plan.',
        confirmButtonText: 'View Plans'
      }).then((result) => {
        if (result.isConfirmed) {
          navigate('/a-jobsubscriptionplans');
        }
      });
      return;
    }

    const nextAvailableAt = getRepostCooldownEnd(jobSeeker);
    if (nextAvailableAt && nextAvailableAt > new Date()) {
      Swal.fire(
        'Error',
        `This candidate is already reposted. It can be reposted again after ${nextAvailableAt.toLocaleString('en-IN')}.`,
        'error'
      );
      return;
    }

    setRepostingUserId(jobSeeker.user_id);
    try {
      await axios.post(`${BaseUrl}/api/job-seeker/${jobSeeker.user_id}/repost`, {
        requester_id: agencyId,
        requester_role: 'agency'
      });

      setJobSeekers(prev => prev.map(item => (
        item.user_id === jobSeeker.user_id
          ? { ...item, reposted_at: new Date().toISOString(), last_reposted_at: new Date().toISOString() }
          : item
      )));

      Swal.fire('Success', `${jobSeeker.first_name || 'Candidate'} reposted successfully.`, 'success');
    } catch (err) {
      const message = err.response?.data?.next_available_at
        ? `This candidate is already reposted. It can be reposted again after ${new Date(err.response.data.next_available_at).toLocaleString('en-IN')}.`
        : err.response?.data?.error || 'Failed to repost candidate';
      Swal.fire('Error', message, 'error');
    } finally {
      setRepostingUserId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Function to parse and format job position
  const formatJobPosition = (jobPosition) => {
    if (!jobPosition) return 'N/A';

    try {
      if (typeof jobPosition === 'string') {
        if (jobPosition.trim().startsWith('[') && jobPosition.trim().endsWith(']')) {
          const parsed = JSON.parse(jobPosition);
          if (Array.isArray(parsed)) {
            return parsed.join(', ');
          }
          return String(parsed);
        }
        return jobPosition;
      } else if (Array.isArray(jobPosition)) {
        return jobPosition.join(', ');
      } else if (jobPosition !== null && jobPosition !== undefined) {
        return String(jobPosition);
      }
    } catch (e) {
      console.error('Error parsing job position:', e);
      if (typeof jobPosition === 'string') {
        return jobPosition;
      }
    }
    return 'N/A';
  };

  // Function to get all contact numbers with country codes
  const getContactNumbers = (jobSeeker) => {
    const contacts = [];

    // WhatsApp
    if (jobSeeker.whatsapp_number) {
      contacts.push({
        type: 'WhatsApp',
        number: `${jobSeeker.whatsapp_country_code || ''} ${jobSeeker.whatsapp_number}`,
        icon: <FaWhatsapp className="text-success" />,
        rawNumber: jobSeeker.whatsapp_number,
        countryCode: jobSeeker.whatsapp_country_code || ''
      });
    }

    // Telegram
    if (jobSeeker.telegram_number) {
      contacts.push({
        type: 'Telegram',
        number: `${jobSeeker.telegram_country_code || ''} ${jobSeeker.telegram_number}`,
        icon: <FaTelegram className="text-info" />,
        rawNumber: jobSeeker.telegram_number,
        countryCode: jobSeeker.telegram_country_code || ''
      });
    }

    // Botim
    if (jobSeeker.botim_number) {
      contacts.push({
        type: 'Botim',
        number: `${jobSeeker.botim_country_code || ''} ${jobSeeker.botim_number}`,
        icon: <FaComment className="text-primary" />,
        rawNumber: jobSeeker.botim_number,
        countryCode: jobSeeker.botim_country_code || ''
      });
    }

    // Mobile Number (if exists and not duplicate)
    if (jobSeeker.mobile_number && !contacts.some(c => c.rawNumber === jobSeeker.mobile_number)) {
      contacts.push({
        type: 'Mobile',
        number: `${jobSeeker.mobile_country_code || ''} ${jobSeeker.mobile_number}`,
        icon: <FaPhone className="text-secondary" />,
        rawNumber: jobSeeker.mobile_number,
        countryCode: jobSeeker.mobile_country_code || ''
      });
    }

    return contacts;
  };

  // Email Popover Component
  const EmailPopover = (email) => (
    <Popover id="email-popover">
      <Popover.Header as="h3">Email Address</Popover.Header>
      <Popover.Body>
        <div className="d-flex align-items-center">
          <FaEnvelope className="me-2 text-primary" />
          <a href={`mailto:${email}`} className="text-decoration-none">{email}</a>
        </div>
      </Popover.Body>
    </Popover>
  );

  // Phone Popover Component
  const PhonePopover = (contacts) => (
    <Popover id="phone-popover" style={{ maxWidth: '300px' }}>
      <Popover.Header as="h3">Contact Numbers</Popover.Header>
      <Popover.Body>
        {contacts.map((contact, idx) => (
          <div key={idx} className="d-flex align-items-center mb-2 pb-2 border-bottom">
            <span className="me-2">{contact.icon}</span>
            <div>
              <small className="text-muted d-block">{contact.type}</small>
              <a
                href={`tel:${contact.rawNumber}`}
                className="text-decoration-none"
                style={{ fontSize: '0.9rem' }}
              >
                {contact.number}
              </a>
            </div>
          </div>
        ))}
        {contacts.length === 0 && (
          <div className="text-muted">No contact numbers available</div>
        )}
      </Popover.Body>
    </Popover>
  );

  if (agencyLoading) {
    return (
      <>
        <AgencyNavbar />
        <Container fluid className="p-3" style={{ marginTop: '70px' }}>
          <Row>
            <Col md={12}>
              <div className="text-center">
                <p>Loading verification status...</p>
              </div>
            </Col>
          </Row>
        </Container>
      </>
    );
  }

  return (
    <>
      <AgencyNavbar />
      <Container fluid className="p-3 agency-jobseekers-container" style={{ marginTop: '70px' }}>
        <Row>
          <Col md={12}>
            <div className="agency-jobseekers-header">
              <div>
                <h2>Job Seekers ({jobSeekers.length})</h2>
                {agencyData && (
                  <div className={`badge ${agencyData.is_verified === 1 ? 'bg-success' : 'bg-warning'} mt-1`}>
                    {agencyData.is_verified === 1 ? 'Verified Agency' : 'Pending Verification'}
                  </div>
                )}
              </div>
              <Button
                onClick={handleRegisterClick}
                variant="primary"
                disabled={agencyLoading}
                className="agency-register-btn d-flex align-items-center gap-2"
              >
                <FaUserPlus /> Job Seeker Registration
              </Button>
            </div>

            {loading && <p>Loading job seekers...</p>}
            {error && <p className="text-danger">Error: {error}</p>}

            {agencyData && agencyData.is_verified === 0 && (
              <div className="alert alert-warning agency-verification-alert" role="alert">
                <strong>Notice:</strong> Your agency account is pending verification. You can view existing job seekers but cannot register new ones until admin approval.
              </div>
            )}

            {jobSeekers.length === 0 && !loading ? (
              <div className="text-center py-4 agency-no-data">
                <p className="text-muted">No job seekers found</p>
                {agencyData && agencyData.is_verified === 1 && (
                  <p className="text-muted">Click "Job Seeker Registration" to add your first job seeker</p>
                )}
              </div>
            ) : (
              <div className="agency-table-responsive-wrapper">
                <style jsx>{`
  .agency-col-job-position,
  .agency-col-country {
    max-width: 150px;
    white-space: normal;
    word-wrap: break-word;
  }
  .agency-col-email,
  .agency-col-phone {
    width: 60px !important;
    min-width: 60px !important;
    max-width: 60px !important;
    text-align: center;
    padding: 0.5rem 0.25rem !important;
  }
  .contact-icon-btn {
    background: none;
    border: none;
    padding: 6px;
    cursor: pointer;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    margin: 0 auto;
  }
  .contact-icon-btn:hover {
    background-color: #f0f0f0;
  }
  .email-icon-btn {
    color: #0d6efd;
  }
  .phone-icon-btn {
    color: #28a745;
  }
`}</style>

                <Table striped bordered hover responsive className="agency-jobseekers-table">
                  <thead className="agency-table-header">
                    <tr>
                      <th className="agency-col-sr">#</th>
                      <th className="agency-col-name">Name</th>
                      <th className="agency-col-job-position" style={{ width: '150px' }}>Job Position</th>
                      <th className="agency-col-country" style={{ width: '120px' }}>Country</th>
                      <th className="agency-col-email" style={{ width: '60px', textAlign: 'center' }}>Email</th>
                      <th className="agency-col-phone" style={{ width: '60px', textAlign: 'center' }}>Phone</th>
                      <th className="agency-col-date">Registered On</th>
                      <th className="agency-col-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="agency-table-body">
                    {jobSeekers.map((jobSeeker, index) => {
                      const jobPosition = formatJobPosition(jobSeeker.job_position);
                      const nationality = jobSeeker.nationality || 'N/A';
                      const email = jobSeeker.email_id || jobSeeker.email || 'N/A';
                      const fullName = `${jobSeeker.first_name || ''} ${jobSeeker.last_name || ''}`.trim() || 'N/A';
                      const contactNumbers = getContactNumbers(jobSeeker);

                      // Debug log to see the user_id for each job seeker
                      console.log(`Job Seeker ${fullName} - job_seeker_id: ${jobSeeker.id}, user_id: ${jobSeeker.user_id}`);

                      return (
                        <tr key={jobSeeker.id} className="agency-table-row">
                          <td className="agency-col-sr">{index + 1}</td>
                          <td className="agency-col-name">{fullName}</td>
                          <td className="agency-col-job-position" style={{ maxWidth: '150px', whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            {jobPosition}
                          </td>
                          <td className="agency-col-country" style={{ maxWidth: '120px', whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            {nationality}
                          </td>
                          <td className="agency-col-email text-center">
                            {email !== 'N/A' ? (
                              <OverlayTrigger
                                trigger={['click', 'focus']}
                                placement="left"
                                overlay={EmailPopover(email)}
                                rootClose
                              >
                                <button className="contact-icon-btn email-icon-btn" title="Click to view email">
                                  <FaEnvelope size={18} />
                                </button>
                              </OverlayTrigger>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td className="agency-col-phone text-center">
                            {contactNumbers.length > 0 ? (
                              <OverlayTrigger
                                trigger={['click', 'focus']}
                                placement="left"
                                overlay={PhonePopover(contactNumbers)}
                                rootClose
                              >
                                <button className="contact-icon-btn phone-icon-btn" title="Click to view contact numbers">
                                  <FaPhone size={16} />
                                </button>
                              </OverlayTrigger>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td className="agency-col-date">{formatDate(jobSeeker.created_at)}</td>
                          <td className="agency-col-actions">
                            <div className="agency-action-buttons">
                              <Button
                                variant="info"
                                size="sm"
                                onClick={() => handleViewResumeClick(jobSeeker)} // Pass the entire jobSeeker object
                                title="View Resume"
                                className="agency-btn-view"
                              >
                                <FaEye />
                              </Button>
                              <Button
                                variant="warning"
                                size="sm"
                                onClick={() => handleRepostJobSeeker(jobSeeker)}
                                title={`Repost Candidate (User ID: ${jobSeeker.user_id})`}
                                className="agency-btn-update"
                                style={{
                                  opacity: hasAgencyPremiumRepostAccess() && !isInRepostCooldown(jobSeeker) ? 1 : 0.55
                                }}
                                disabled={
                                  (agencyData && agencyData.is_verified === 0) ||
                                  repostingUserId === jobSeeker.user_id
                                }
                              >
                                <FaSync /> <span className="agency-btn-text">{repostingUserId === jobSeeker.user_id ? 'Reposting' : isInRepostCooldown(jobSeeker) ? 'Reposted' : 'Repost'}</span>
                              </Button>
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => handleUpdateResumeClick(jobSeeker)}
                                title={`Update Resume (User ID: ${jobSeeker.user_id})`}
                                className="agency-btn-update"
                                disabled={agencyData && agencyData.is_verified === 0}
                              >
                                <FaEdit /> <span className="agency-btn-text">Update</span>
                              </Button>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => handleDeleteJobSeeker(jobSeeker)}
                                title={`Delete Candidate (User ID: ${jobSeeker.user_id})`}
                                className="agency-btn-delete"
                                disabled={agencyData && agencyData.is_verified === 0}
                              >
                                <FaTrash />
                              </Button>
                            </div>
                            {agencyData && agencyData.is_verified === 0 && (
                              <small className="text-muted d-block text-center mt-1 agency-update-disabled">
                                Repost, Update & Delete disabled
                              </small>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default AddJobSeeker;
